import Typography from "@mui/joy/Typography";
import { Grid, Stack } from "@mui/joy";
import { Beliefs, conceptApi, ConceptFieldKey } from "api/conceptApi";
import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRecoilValue } from "recoil";
import { patientAtom } from "recoil/patientAtom";
import ConceptNoteInput from "../ConceptNoteInput";
import ConceptNoteConfirmButton from "../ConceptNoteConfirmButton";

export default function CoreBeliefsItem({
  beliefs,
  setBeliefs,
}: {
  beliefs: Beliefs;
  setBeliefs: React.Dispatch<React.SetStateAction<Beliefs | undefined>>;
  /** 변경사항을 상위로 전달(옵션) */
}) {
  const patientState = useRecoilValue(patientAtom);
  const [canBeConfirmed, setCanBeConfirmed] = useState<boolean>(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    setCanBeConfirmed(
      (beliefs.coreBeliefs?.self?.trim() ?? "").length > 0 ||
        (beliefs.coreBeliefs?.others?.trim() ?? "").length > 0 ||
        (beliefs.coreBeliefs?.world?.trim() ?? "").length > 0
    );
  }, [beliefs]);

  const { mutate: updateNote } = useMutation<
    void, // 성공 응답 타입
    unknown, // 에러 타입
    ConceptFieldKey
  >(
    async (payload) => {
      await conceptApi.noteUpdate(patientState?.patientId!!, payload);
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error("Note update failed:", error);
      },
    }
  );

  const handleSaveCoreBeliefsSelf = (newValue: string) => {
    setBeliefs((prev) => ({
      ...prev,
      coreBeliefs: {
        ...prev?.coreBeliefs,
        self: newValue,
      },
    }));
    updateNote({
      coreBeliefsSelf: newValue,
    });
  };

  const handleSaveCoreBeliefsOthers = (newValue: string) => {
    setBeliefs((prev) => ({
      ...prev,
      coreBeliefs: {
        ...prev?.coreBeliefs,
        others: newValue,
      },
    }));
    updateNote({
      coreBeliefsOthers: newValue,
    });
  };

  const handleSaveCoreBeliefsWorld = (newValue: string) => {
    setBeliefs((prev) => ({
      ...prev,
      coreBeliefs: {
        ...prev?.coreBeliefs,
        world: newValue,
      },
    }));
    updateNote({
      coreBeliefsWorld: newValue,
    });
  };

  return (
    <Stack spacing={1}>
      <Stack direction={"row"} justifyContent="space-between">
        <Stack direction={"row"} spacing={1}>
          <Typography variant={"Footnote"} sx={{ fontWeight: 500 }}>
            {"핵심 믿음"}
          </Typography>

          {(beliefs.coreBeliefsStatus === "note_confirmed" ||
            beliefs.coreBeliefsStatus === "confirmed") && (
            <Stack
              justifyContent="center"
              sx={{ color: "primary.solid", width: "16px", height: "16px" }}
            >
              <span
                style={{ fontSize: "16px" }}
                className="material-symbols-rounded"
              >
                check
              </span>
            </Stack>
          )}
        </Stack>

        {/* 우측 액션(삭제, 확정) */}
        <Stack direction="row" spacing={1}>
          {beliefs.coreBeliefsStatus === "preparing" && (
            <ConceptNoteConfirmButton
              conceptNoteKey={"coreBeliefs"}
              label="핵심 믿음"
              disabled={!canBeConfirmed}
            />
          )}
        </Stack>
      </Stack>

      <Grid container>
        <Grid xs={2.5} sx={{ pt: "10px" }}>
          <Typography variant={"Footnote"} sx={{ fontWeight: 500, pl: 0.8 }}>
            나
          </Typography>
        </Grid>
        <Grid xs={9.5}>
          <ConceptNoteInput
            referenceByConceptFieldKey={"coreBeliefsSelf"}
            initialValue={beliefs.coreBeliefs?.self}
            onSave={handleSaveCoreBeliefsSelf}
          />
        </Grid>
      </Grid>

      <Grid container>
        <Grid xs={2.5} sx={{ pt: "10px" }}>
          <Typography variant={"Footnote"} sx={{ fontWeight: 500, pl: 0.8 }}>
            타인
          </Typography>
        </Grid>
        <Grid xs={9.5}>
          <ConceptNoteInput
            referenceByConceptFieldKey={"coreBeliefsOthers"}
            initialValue={beliefs.coreBeliefs?.others}
            onSave={handleSaveCoreBeliefsOthers}
          />
        </Grid>
      </Grid>

      <Grid container>
        <Grid xs={2.5} sx={{ pt: "10px" }}>
          <Typography variant={"Footnote"} sx={{ fontWeight: 500, pl: 0.8 }}>
            세상
          </Typography>
        </Grid>
        <Grid xs={9.5}>
          <ConceptNoteInput
            referenceByConceptFieldKey={"coreBeliefsWorld"}
            initialValue={beliefs.coreBeliefs?.world}
            onSave={handleSaveCoreBeliefsWorld}
          />
        </Grid>
      </Grid>
    </Stack>
  );
}
